// Season editor "Sensors" section: the sensors table (Type | ID | Block),
// the selected sensor's editable details beside it, and a map with the
// sensor pins. It replaces Edit Field's sensors section, so a sensor is
// edited, added, linked, toggled and deleted exactly as there (rules in
// sensorEditRules.js).
//
// Sensors come from each season's detail (GET v2/seasons/...); changes go to
// the season editor (useSeasonSensors) and are written by "Save changes".
// Detail edits apply as the user types — the equivalent of Edit Field's
// "Done"; "Add new" keeps its own Cancel / Done.
const React = require('react');
const { useState, useMemo, useEffect, useRef, useCallback } = require('react');
const { useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const GeneralClasses = require('GeneralClasses');
const locationManager = require('locationManager');
const {
  get_configuration_config,
  FeatureFlagDictionary,
} = require('remoteConfigurationsManager');
const BlocksMap = require('./BlocksMap');
const rules = require('./sensorEditRules');
const {
  SensorFields,
  SensorSettings,
  NewSensorFooter,
  SensorsCard,
} = require('./SensorEditor');

const sensorRowsFor = (rows, sensorsBySeason) =>
  rows.reduce((sensorRows, row) => {
    const sensors = sensorsBySeason[row.season.id] || {};
    Object.keys(sensors).forEach((serial) => {
      sensorRows.push({
        key: row.season.id + ':' + serial,
        seasonId: row.season.id,
        serial,
        sensor: sensors[serial],
        blockName: row.block.name,
      });
    });
    return sensorRows;
  }, []);

const DetailStatus = ({ rows, details }) => {
  const { t } = useTranslation();
  const failed = rows.find((row) => details[row.season.id]?.error);
  if (failed) {
    return (
      <div className='ns-error-text'>
        {t('new_season_detail_failed', {
          message: details[failed.season.id].error,
        })}
      </div>
    );
  }
  return rows.some((row) => !details[row.season.id]) ? (
    <div className='ns-muted'>{t('loading')}</div>
  ) : null;
};

const coordinatesOf = (sensor) => {
  const lat = parseFloat(sensor?.coordinates?.latitude);
  const lng = parseFloat(sensor?.coordinates?.longitude);
  return isNaN(lat) || isNaN(lng) ? null : { lat, lng };
};

const SensorsTable = ({ sensorRows, selectedKey, onSelect }) => {
  const { t } = useTranslation();
  if (sensorRows.length === 0) {
    return <div className='ns-empty-text'>{t('new_season_no_sensors')}</div>;
  }
  return (
    <table className='ns-table'>
      <thead>
        <tr>
          <th>{t('type')}</th>
          <th>{t('new_season_sensor_id')}</th>
          <th>{t('new_season_block')}</th>
        </tr>
      </thead>
      <tbody>
        {sensorRows.map((row) => (
          <tr
            key={row.key}
            className={row.key === selectedKey ? 'ns-table-row-selected' : ''}
            onClick={() => onSelect(row.key)}
          >
            <td>{rules.sensorTypeTitle(row.sensor)}</td>
            <td>{row.sensor.transmitter_id || row.serial}</td>
            <td>{row.blockName}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// The values the editor needs from the store, as Edit Field reads them.
const useSensorEditContext = (farmingUnitName, distributionName) => {
  const userData = useSelector((state) => state.authentication_data?.user_data);
  const fertilizerData = useSelector(
    (state) => state.fertilizer_data?.fertilizer_data,
  );
  const configurationData = useSelector((state) => state.configuration_data);
  const showTensiometerRange = Boolean(
    get_configuration_config(
      configurationData || {},
      FeatureFlagDictionary.TENSIOMETER_100CBAR,
      farmingUnitName,
      distributionName,
    ),
  );
  const defaultBoundaries =
    userData?.length === 'Inch'
      ? GeneralClasses.DEFAULT_HIGH_PRESSURE_BOUNDARIES_PSI
      : GeneralClasses.DEFAULT_HIGH_PRESSURE_BOUNDARIES;
  return {
    lengthUnit: userData?.length,
    fertilizerData,
    showTensiometerRange,
    defaultBoundaries,
  };
};

// The selected existing sensor: its draft and the sensor it was opened from.
// Each draft change re-applies the draft to that sensor (Edit Field's Done).
const useSelectedSensor = (selected, onUpdateSensors, defaultBoundaries) => {
  const [draft, setDraft] = useState(null);
  const baseRef = useRef(null);
  const selectedKey = selected?.key;
  useEffect(() => {
    baseRef.current = selected ? rules.cloneSensor(selected.sensor) : null;
    setDraft(
      selected
        ? rules.draftFromSensor(selected.sensor, defaultBoundaries)
        : null,
    );
    // Re-open only when another sensor is selected, not on every change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const changeSensor = useCallback(
    (change) => {
      if (!selected) {
        return;
      }
      onUpdateSensors(selected.seasonId, (sensors) => {
        sensors[selected.serial] = change(sensors[selected.serial]);
        return sensors;
      });
    },
    [selected, onUpdateSensors],
  );

  const onDraftChange = useCallback(
    (nextDraft) => {
      setDraft(nextDraft);
      changeSensor(() =>
        rules.applyDraft(rules.cloneSensor(baseRef.current), nextDraft),
      );
    },
    [changeSensor],
  );

  // A setting changes the sensor and the base the draft is applied to.
  const changeSetting = useCallback(
    (apply) => {
      baseRef.current = apply(baseRef.current);
      changeSensor((sensor) => apply(sensor));
    },
    [changeSensor],
  );

  return { draft, onDraftChange, changeSetting };
};

// "Add new": its draft and the season the sensor goes to.
const useNewSensor = (rows, defaultBoundaries) => {
  const [draft, setDraft] = useState(null);
  const [seasonId, setSeasonId] = useState(null);
  const open = () => {
    setDraft(rules.newSensorDraft(defaultBoundaries));
    setSeasonId(rows[0]?.season.id ?? null);
  };
  const close = useCallback(() => setDraft(null), []);
  const onTypeChange = (type) =>
    setDraft(rules.emptyDraft({ type }, defaultBoundaries));
  return { draft, setDraft, seasonId, setSeasonId, open, close, onTypeChange };
};

const seasonChoiceFor = (rows, newSensor) =>
  rows.length > 1
    ? {
        value: newSensor.seasonId,
        options: rows.map((row) => ({
          title: row.block.name,
          value: row.season.id,
        })),
        onChange: newSensor.setSeasonId,
      }
    : null;

const canAddSensor = (draft) =>
  (Boolean(draft.transmitterId) || rules.isAIIrrigator(draft)) &&
  !rules.isDraftInvalid(draft);

// Save is blocked while "Add new" is open (Edit Field blocks Update the same
// way) or the sensor being edited has invalid pressure boundaries.
const useBlockingReport = (newDraft, draft, onBlockingChange, t) => {
  const reason = newDraft
    ? t('edit_field_finish_open_edit_message')
    : draft && rules.isDraftInvalid(draft)
      ? t('max_bigger_than_min')
      : null;
  useEffect(() => {
    onBlockingChange(reason);
  }, [reason, onBlockingChange]);
  useEffect(() => () => onBlockingChange(null), [onBlockingChange]);
};

// Sensor.jsx's row menu, for the selected sensor.
const settingsActionsFor = (selected, editing, onUpdateSensors, deselect) => ({
  onToggleIgnore: () =>
    editing.changeSetting((sensor) =>
      rules.setIgnoreInCalculations(sensor, !sensor.ignore_in_calculations),
    ),
  onTogglePaused: () =>
    editing.changeSetting((sensor) => rules.setPaused(sensor, !sensor.paused)),
  onToggleWFR: () =>
    editing.changeSetting((sensor) =>
      rules.setConsiderAsWFR(sensor, !sensor.should_consider_as_wfr),
    ),
  onLocate: () =>
    locationManager
      .get_user_location()
      .then((position) =>
        editing.onDraftChange({
          ...editing.draft,
          ...rules.locatedCoordinates(position),
        }),
      )
      .catch((error) => console.log(error)),
  onDelete: () => {
    onUpdateSensors(selected.seasonId, (sensors) => {
      delete sensors[selected.serial];
      return sensors;
    });
    deselect();
  },
});

const NewSensorPanel = ({ rows, newSensor, editorProps, onAdd }) => (
  <div className='ns-sensor-details'>
    <SensorFields
      {...editorProps}
      draft={newSensor.draft}
      isNew
      onDraftChange={newSensor.setDraft}
      onTypeChange={newSensor.onTypeChange}
      valvesKey='new'
    />
    <NewSensorFooter
      seasonChoice={seasonChoiceFor(rows, newSensor)}
      canAdd={canAddSensor(newSensor.draft)}
      onAdd={onAdd}
      onCancel={newSensor.close}
    />
  </div>
);

const SelectedSensorPanel = ({ selected, editing, editorProps, actions }) => (
  <div className='ns-sensor-details'>
    <SensorFields
      {...editorProps}
      draft={editing.draft}
      isNew={false}
      onDraftChange={editing.onDraftChange}
      valvesKey={selected.key}
    />
    <SensorSettings sensor={selected.sensor} actions={actions} />
  </div>
);

const useSensorMarkers = (sensorRows, selectedKey, onSelect) =>
  useMemo(
    () =>
      sensorRows
        .map((row) => ({ row, coordinates: coordinatesOf(row.sensor) }))
        .filter((item) => item.coordinates)
        .map(({ row, coordinates }) => ({
          key: row.key,
          lat: coordinates.lat,
          lng: coordinates.lng,
          title: row.sensor.name || row.serial,
          selected: row.key === selectedKey,
          onClick: () => onSelect(row.key),
        })),
    [sensorRows, selectedKey, onSelect],
  );

const SensorsSection = ({
  rows,
  details,
  mapBlocks,
  selectedBlockKeys,
  sensorsBySeason,
  farmingUnitSensors,
  onUpdateSensors,
  onBlockingChange,
  farmingUnitName,
  distributionName,
}) => {
  const { t } = useTranslation();
  const context = useSensorEditContext(farmingUnitName, distributionName);
  const sensorRows = useMemo(
    () => sensorRowsFor(rows, sensorsBySeason),
    [rows, sensorsBySeason],
  );
  // Nothing is selected until the user picks a sensor (row or map pin).
  const [selectedKey, setSelectedKey] = useState(null);
  const selected = sensorRows.find((row) => row.key === selectedKey) || null;
  const editing = useSelectedSensor(
    selected,
    onUpdateSensors,
    context.defaultBoundaries,
  );
  const newSensor = useNewSensor(rows, context.defaultBoundaries);
  useBlockingReport(newSensor.draft, editing.draft, onBlockingChange, t);

  // Clicking the selected sensor again deselects it.
  const closeNewSensor = newSensor.close;
  const toggleSelected = useCallback(
    (key) => {
      closeNewSensor();
      setSelectedKey((current) => (current === key ? null : key));
    },
    [closeNewSensor],
  );
  const openNewSensor = () => {
    setSelectedKey(null);
    newSensor.open();
  };
  const addNewSensor = () => {
    const seasonId = newSensor.seasonId;
    const added = rules.newSensorFromDraft(
      newSensor.draft,
      sensorsBySeason[seasonId],
      GeneralClasses,
    );
    if (added) {
      onUpdateSensors(seasonId, (sensors) => {
        sensors[added.id] = added.sensor;
        return sensors;
      });
    }
    newSensor.close();
  };

  const markers = useSensorMarkers(sensorRows, selected?.key, toggleSelected);
  const detailsLoaded = rows.every((row) => details[row.season.id]?.season);
  const isOpen = Boolean((selected && editing.draft) || newSensor.draft);
  const editorProps = {
    farmingUnitSensors,
    fertilizerData: context.fertilizerData,
    lengthUnit: context.lengthUnit,
    showTensiometerRange: context.showTensiometerRange,
  };
  return (
    <div className='ns-section d-flex flex-column'>
      <div className='ns-section-title'>{t('sensors')}</div>
      <div className='ns-sensors-section'>
        <SensorsCard
          isOpen={isOpen}
          addDisabled={!detailsLoaded || Boolean(newSensor.draft)}
          onAdd={openNewSensor}
          details={
            newSensor.draft ? (
              <NewSensorPanel
                rows={rows}
                newSensor={newSensor}
                editorProps={editorProps}
                onAdd={addNewSensor}
              />
            ) : (
              selected &&
              editing.draft && (
                <SelectedSensorPanel
                  selected={selected}
                  editing={editing}
                  editorProps={editorProps}
                  actions={settingsActionsFor(
                    selected,
                    editing,
                    onUpdateSensors,
                    () => setSelectedKey(null),
                  )}
                />
              )
            )
          }
        >
          <DetailStatus rows={rows} details={details} />
          <SensorsTable
            sensorRows={sensorRows}
            selectedKey={selected?.key}
            onSelect={toggleSelected}
          />
        </SensorsCard>
        <div className='ns-section-body'>
          <BlocksMap
            className='ns-map-small'
            blocks={mapBlocks}
            selectedKeys={selectedBlockKeys}
            focusKeys={selectedBlockKeys}
            markers={markers}
          />
        </div>
      </div>
    </div>
  );
};

module.exports = SensorsSection;
module.exports.useSensorEditContext = useSensorEditContext;
